"use client";
import React, { createContext, useContext, useEffect, useState } from "react";
import { BASE_URL } from "../config/apiConfig";

const SubscriptionContext = createContext();
export const useSubscription = () => useContext(SubscriptionContext);

export const SubscriptionProvider = ({ children }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem("sub_cart");
    return savedCart ? JSON.parse(savedCart) : [];
  });

  const [token, setToken] = useState(() => localStorage.getItem("auth_token"));

  // Track subscription status
  const [hasSubscribedBefore, setHasSubscribedBefore] = useState(false); // ever subscribed
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false); // currently active

  // Fetch subscription plans whenever token changes
  useEffect(() => {
    if (!token) return;

    setLoading(true);
    fetch(`${BASE_URL}/subscriptions`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === 200 && Array.isArray(data.data)) {
          const formatted = data.data.map((plan) => ({
            id: plan.id,
            name: plan.title,
            price: `${plan.amount}`,
            per: "Month",
            features: JSON.parse(plan.features),
            status: plan.status,
          }));
          setPlans(formatted);
        } else {
          console.error("Unexpected response format:", data);
        }
      })
      .catch((err) => console.error("Failed to load subscription plans:", err))
      .finally(() => setLoading(false));
  }, [token]);

  // Fetch user's subscription status (active / ever subscribed)
  useEffect(() => {
    if (!token) return;
    const userId = localStorage.getItem("user_id");
    if (!userId) return;

    fetch(`${BASE_URL}/user-subscription-status/${userId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === 200) {
          setHasSubscribedBefore(data.has_subscription_before === true);
          setHasActiveSubscription(data.has_active_subscription === true);
        }
      })
      .catch((err) =>
        console.error("Error fetching subscription status:", err)
      );
  }, [token]);

  // Update token (for login)
  const updateToken = (newToken) => {
    localStorage.setItem("auth_token", newToken);
    setToken(newToken); // triggers plan & status fetch
  };

  // Cart functions
  const addToCart = (plan) => {
    setCart((prevCart) => {
      if (prevCart.length > 0) {
        alert("You can only have one subscription plan in the cart at a time.");
        return prevCart;
      }
      const updatedPlan = { ...plan, quantity: 1, price: plan.price };
      const updated = [updatedPlan];
      localStorage.setItem("sub_cart", JSON.stringify(updated));
      return updated;
    });
  };

  const removeFromSubCart = (planId) => {
    setCart((prevCart) => {
      const updated = prevCart.filter((item) => item.id !== planId);
      localStorage.setItem("sub_cart", JSON.stringify(updated));
      return updated;
    });
  };

  return (
    <SubscriptionContext.Provider
      value={{
        plans,
        loading,
        selectedPlan,
        setSelectedPlan,
        cart,
        addToCart,
        removeFromSubCart,
        token,
        updateToken,
        hasSubscribedBefore,
        hasActiveSubscription,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};
